import "./PageHeader.style.css";

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppRouter } from "../../../utils/enums/AppRouter";
import { AuthorizationStatus } from "../../../utils/enums/AuthorizationStatus";
import { ApiFactory } from "../../../api-client/api-factory";
import { UsersApi } from "../../../api-client";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { requireAuthorization } from "../../../store/actions/appActions";
import { Button, ButtonColor } from "../../base/Button/Button";
import { Switcher } from "../../base/Switcher/Switcher";
import { useIsMobile } from "../../../utils/functions/useIsMobile";
import { getCurrentPage } from "../../../utils/functions/getCurrentPage";

import { IconUser24 } from "../../icons/IconUser/IconUser24";
import { LoginIcon24 } from "../../icons/LoginIcon24";
import { DoorExitIcon24 } from "../../icons/DoorExitIcon24";

const PAGES_WITHOUT_NAVIGATION = [
  AppRouter.Login,
  AppRouter.Game,
  AppRouter.NotFoundPage,
  AppRouter.Secret,
];

interface PageHeaderProps {
  username: string;
  setUsername: (newUsername: string) => void;
}

export const PageHeader = ({ username, setUsername }: PageHeaderProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const authorizationStatus = useAppSelector(
    (state) => state.user.authorizationStatus,
  );

  const path = useLocation().pathname.split("/").slice(1);
  const [currentPage, setCurrentPage] = useState<AppRouter>(
    AppRouter.NotFoundPage,
  );
  const showNavigation = !PAGES_WITHOUT_NAVIGATION.includes(currentPage);

  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const [isAccountMenuOpened, setIsAccountMenuOpened] =
    useState<boolean>(false);

  useEffect(() => {
    setCurrentPage(getCurrentPage(path));
  }, [path]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleAccountButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsAccountMenuOpened((prevState) => !prevState);
  };

  const handleClickOutside = (e: MouseEvent) => {
    const isAccountMenu =
      accountMenuRef.current &&
      accountMenuRef.current.contains(e.target as Node);

    if (!isAccountMenu) {
      setIsAccountMenuOpened(false);
      setIsAccountMenuOpened(false);
    }
  };

  const handleLogoutClicked = () => {
    ApiFactory.get(UsersApi)
      .logoutGet()
      .then(() => {
        setUsername("");
        dispatch(requireAuthorization(AuthorizationStatus.NoAuth));
      })
      .catch(() => console.error("Не получилось выйти из аккаунта"));

    setIsAccountMenuOpened(false);
  };

  return (
    <header className="header">
      <Link className="header__logo-link" to={AppRouter.Main}>
        <img className="header__logo" src="/icons/logo.svg" alt="Yourdle" />
      </Link>

      {showNavigation && !isMobile && (
        <>
          <nav className="header__nav">
            <Switcher
              startingItem={0}
              items={[
                {
                  content: "Галерея",
                  onActive: () => navigate(AppRouter.Gallery),
                },
                {
                  content: "Конструктор",
                  onActive: () => navigate(AppRouter.MyGames),
                },
              ]}
            />
          </nav>
          <div className="header__options">
            {authorizationStatus === AuthorizationStatus.Auth ? (
              <Button
                className="header__account-button"
                text={username}
                icon={<IconUser24 />}
                color={ButtonColor.Primary}
                onClick={handleAccountButtonClick}
              />
            ) : (
              <Button
                className="header__login-button"
                text={"Войти в аккаунт"}
                icon={<LoginIcon24 />}
                color={ButtonColor.Border}
                onClick={() => navigate(AppRouter.Login)}
              />
            )}
            <div
              ref={accountMenuRef}
              className={`header__account-menu ${
                isAccountMenuOpened ? "header__account-menu_opened" : ""
              }`}
            >
              <Button
                className="header__account-menu__logout-button"
                text={"Выйти"}
                icon={<DoorExitIcon24 />}
                color={ButtonColor.Secondary}
                onClick={handleLogoutClicked}
              />
            </div>
          </div>
        </>
      )}
    </header>
  );
};
